import { FC } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { CustomButton } from "../../../atoms/button/button";
import { Colors } from "../../../../configs";
import { IStoreClosure } from "../../../../util/store-closures";

interface IReadyDateRow {
  label: string;
  date: Dayjs;
  isAdminDate: boolean;
  affected: boolean;
}

export interface IHolidayDatesDialogProps {
  open: boolean;
  onClose: () => void;
  holidays: IStoreClosure[];
  special: Omit<IReadyDateRow, "label">;
  regular: Omit<IReadyDateRow, "label">;
}

/**
 * Tells the cashier which holidays the Special / Regular dates skip and the
 * dates used instead (reported issues 3.f). Admin-set dates are marked.
 */
export const HolidayDatesDialog: FC<IHolidayDatesDialogProps> = ({
  open,
  onClose,
  holidays,
  special,
  regular,
}) => {
  const rows: IReadyDateRow[] = [
    { label: "Special", ...special },
    { label: "Regular", ...regular },
  ];
  const textSx = { fontSize: "var(--fs-base)" };
  const noteSx = { fontSize: "var(--fs-xs)", color: Colors.TEXT_GRAY_DARK };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="holiday-dates-title">
      <DialogTitle id="holiday-dates-title" component="div">
        <Typography component="h2" sx={{ fontSize: "var(--fs-lg)", fontWeight: 600 }}>
          Holidays affect the ready date
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "1.6rem" }}>
        <Box>
          <Typography sx={{ ...textSx, fontWeight: 600, mb: "0.6rem" }}>
            The store is closed on:
          </Typography>
          {holidays.map((h) => (
            <Typography key={h.date} sx={textSx} data-testid="holiday-row">
              {dayjs(h.date).format("ddd, MMM D")}
              {h.reason ? ` — ${h.reason}` : ""}
            </Typography>
          ))}
        </Box>
        <Box>
          <Typography sx={{ ...textSx, fontWeight: 600, mb: "0.6rem" }}>
            Next possible ready dates:
          </Typography>
          {rows.map((row) => (
            <Box
              key={row.label}
              data-testid={`holiday-ready-${row.label.toLowerCase()}`}
              sx={{ display: "flex", gap: "1rem", alignItems: "baseline" }}
            >
              <Typography sx={{ ...textSx, width: "7rem", fontWeight: 600 }}>
                {row.label}
              </Typography>
              <Typography sx={textSx}>
                {row.date.format("ddd, MMM D · h:mm A")}
              </Typography>
              {row.affected && (
                <Typography sx={noteSx}>
                  {row.isAdminDate ? "Set by admin" : "Next open day"}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: "2.4rem", pb: "1.6rem" }}>
        <CustomButton sx={{ px: "3rem", fontSize: "var(--fs-base)" }} onClick={onClose}>
          OK
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};
